import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';

const BreakdownTable = ({ breakdownData, nameColumnLabel }) => {
  // nameColumnLabel: "Section Name" when sectionName is key, "Ring Name" when ringName is key
  const headerLabel = nameColumnLabel || 'Name';

  const { totalLength, totalInvoiceAmount } = useMemo(() => {
    const parseLen = (val) => {
      if (val === null || val === undefined) {
        return 0;
      }
      const cleaned = String(val).replace(/,/g, '').trim();
      const num = cleaned && !Number.isNaN(parseFloat(cleaned)) ? parseFloat(cleaned) : 0;
      return num;
    };

    const parseAmount = (val) => {
      if (val === null || val === undefined) {
        return 0;
      }
      const cleaned = String(val).replace(/,/g, '').trim();
      const num = cleaned && !Number.isNaN(parseFloat(cleaned)) ? parseFloat(cleaned) : 0;
      return num;
    };

    let total = 0;
    let totalAmount = 0;

    (breakdownData || []).forEach((row) => {
      const len = parseLen(row.length);
      const amt = parseAmount(row.invoiceAmount);
      total += len;
      totalAmount += amt;
    });

    return {
      totalLength: total,
      totalInvoiceAmount: totalAmount,
    };
  }, [breakdownData]);

  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        sx={{
          fontSize: '16px',
          fontFamily: 'Inter',
          fontWeight: 600,
          mb: 2,
          color: '#192126',
          display: 'flex',
        }}
      >
        Breakdown
      </Typography>
      {/* Totals for ring lengths */}
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: 'none',
          border: '1px solid #E8E9E9',
          borderRadius: '8px',
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#F8F9FA' }}>
              <TableCell
                sx={{
                  fontSize: '14px',
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  color: '#344054',
                  borderBottom: '1px solid #E8E9E9',
                }}
              >
                {headerLabel}
              </TableCell>
              <TableCell
                sx={{
                  fontSize: '14px',
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  color: '#344054',
                  borderBottom: '1px solid #E8E9E9',
                }}
              >
                Milestone
              </TableCell>
              <TableCell
                sx={{
                  fontSize: '14px',
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  color: '#344054',
                  borderBottom: '1px solid #E8E9E9',
                }}
              >
                Length
              </TableCell>
              <TableCell
                sx={{
                  fontSize: '14px',
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  color: '#344054',
                  borderBottom: '1px solid #E8E9E9',
                }}
              >
                Unit rate
              </TableCell>
              <TableCell
                sx={{
                  fontSize: '14px',
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  color: '#344054',
                  borderBottom: '1px solid #E8E9E9',
                }}
              >
                Invoice Amount
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {breakdownData.map((row, index) => (
              <TableRow key={index}>
                <TableCell
                  sx={{
                    fontSize: '14px',
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    color: '#192126',
                    borderBottom: '1px solid #E8E9E9',
                  }}
                >
                  {row.name}
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: '14px',
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    color: '#192126',
                    borderBottom: '1px solid #E8E9E9',
                  }}
                >
                  {row.milestone ?? '—'}
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: '14px',
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    color: '#192126',
                    borderBottom: '1px solid #E8E9E9',
                  }}
                >
                  {row.length}
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: '14px',
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    color: '#192126',
                    borderBottom: '1px solid #E8E9E9',
                  }}
                >
                  {row.unitRate}
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: '14px',
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    color: '#192126',
                    borderBottom: '1px solid #E8E9E9',
                  }}
                >
                  {row.invoiceAmount}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {breakdownData && breakdownData.length > 0 && (
        <TableContainer
          component={Paper}
          sx={{
            mb: 1.5,
            boxShadow: 'none',
            border: '1px solid #E8E9E9',
            borderRadius: '8px',
            overflow: 'hidden',
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F8F9FA' }}>
                <TableCell
                  sx={{
                    fontSize: '12px',
                    fontFamily: 'Inter',
                    fontWeight: 600,
                    color: '#344054',
                    borderBottom: '1px solid #E8E9E9',
                  }}
                >
                  Metric
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontSize: '12px',
                    fontFamily: 'Inter',
                    fontWeight: 600,
                    color: '#344054',
                    borderBottom: '1px solid #E8E9E9',
                  }}
                >
                  Value
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontSize: '12px', color: '#192126' }}>Total Length</TableCell>
                <TableCell
                  align="right"
                  sx={{ fontSize: '12px', fontWeight: 600, color: '#192126' }}
                >
                  {(totalLength || 0).toLocaleString()}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell sx={{ fontSize: '12px', color: '#192126' }}>
                  Total Invoice Amount
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontSize: '12px', fontWeight: 600, color: '#192126' }}
                >
                  {(totalInvoiceAmount || 0).toLocaleString('en-US', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                  })}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default BreakdownTable;
